/**
 * Normalize a US/Canada phone number to E.164 format (+1XXXXXXXXXX).
 * Strips non-digit characters, prepends country code if missing.
 * Returns null if the input can't be normalized.
 */
export function normalizePhoneE164(value: string): string | null {
  const digits = value.replace(/[^\d]/g, '');
  const normalized =
    digits.startsWith('1') && digits.length === 11
      ? `+${digits}`
      : digits.length === 10
        ? `+1${digits}`
        : null;
  if (!normalized || !/^\+1\d{10}$/.test(normalized)) return null;
  return normalized;
}

/**
 * True when the raw input looks like a phone number rather than an email.
 * Matches +1234567890, 123-456-7890, (123) 456-7890, etc. — 10 to 15 digits
 * after stripping spaces, dashes, parens and dots, optional leading +.
 *
 * Detection only. This decides the SMS-vs-magic-link branch on both surfaces —
 * web (`LoginModal`) and native (`app/(auth)/login.tsx`) import it from here so
 * the two route identically (Core Rule 6 parity; #2441). E.164 normalization +
 * the US/CA `/^\+1\d{10}$/` gate live in `normalizePhoneE164`, which the caller
 * applies before sending (Core Rule 27).
 */
export function isPhoneNumber(value: string): boolean {
  const cleaned = value.replace(/[\s\-().]/g, '');
  // Must start with + or digit, and have 10-15 digits total.
  return /^\+?\d{10,15}$/.test(cleaned);
}

/**
 * The single user-facing error for an identifier that is neither a valid email
 * nor a valid US/CA phone. Shared verbatim by web (`LoginModal`) and native
 * (`app/(auth)/login.tsx`) so the two surfaces cannot drift, and asserted by
 * both test suites (#2441). House voice: spelled out "US or Canada" to match
 * the sibling "valid US or Canada phone number" copy in the same handlers.
 */
export const LOGIN_IDENTIFIER_ERROR =
  'Enter a valid email address or a US or Canada phone number.';

/**
 * True when the input is made only of phone punctuation (an optional leading +,
 * then digits, spaces, dashes, parens and dots) and has at least one digit —
 * i.e. it is not an email attempt. Internal helper for `isMalformedPhoneAttempt`.
 */
function looksLikePhoneNumber(value: string): boolean {
  const trimmed = value.trim();
  return /^\+?[\d\s().-]+$/.test(trimmed) && /\d/.test(trimmed);
}

/**
 * True when the input is clearly a phone attempt but NOT a valid US/CA number:
 * only phone punctuation, at least 7 digits (enough that a person recognizes it
 * as a phone, so "1" or a 5-digit ZIP still falls through to the email-format
 * check), and `isPhoneNumber` rejects it. Both login surfaces call THIS before
 * any request and show `LOGIN_IDENTIFIER_ERROR`, instead of routing the digits
 * to the email endpoint and surfacing its raw "Invalid request data" (#2441).
 */
export function isMalformedPhoneAttempt(value: string): boolean {
  if (!looksLikePhoneNumber(value)) return false;
  const digitCount = (value.match(/\d/g) ?? []).length;
  return digitCount >= 7 && !isPhoneNumber(value);
}
